import {inject, Injectable, InjectionToken} from '@angular/core';
import {from, Observable} from 'rxjs';
import {DirectusHttpService} from '@app/providers/expo/directus/directus-http.service';
import {
  authentication,
  AuthenticationData,
  createDirectus,
  createItem,
  deleteItem,
  DirectusUser,
  Query,
  readCollection,
  readItem,
  readItems,
  readMe,
  readRole,
  readRoles,
  readUser,
  readUsers,
  realtime,
  rest,
  updateItem,
} from '@directus/sdk';
import {
  DirectusCollection,
  DirectusSchema,
} from '@app/providers/expo/directus/directus-collections.interface';
import {environment} from '@env/environment';

const AUTH_STORAGE_KEY = 'cmsAuth';

/**
 * The fetch the Directus client sends requests with. The SDK captures
 * `globalThis.fetch` when its module loads, so stubbing the global in a spec
 * is too late -- this token is the seam tests replace instead.
 */
export const DIRECTUS_FETCH = new InjectionToken<typeof fetch>(
  'DIRECTUS_FETCH',
  {
    providedIn: 'root',
    factory: () => (input, init) => globalThis.fetch(input, init),
  }
);

function readStoredAuth(): AuthenticationData | null {
  try {
    return JSON.parse(sessionStorage.getItem(AUTH_STORAGE_KEY));
  } catch {
    return null;
  }
}

/**
 * The SDK's refresh() blanks the stored tokens *before* it sends the refresh
 * request and never puts them back if that request fails. One dropped request
 * (wifi blip, tablet waking from sleep, Directus/nginx timeout) therefore
 * leaves no refresh token, every later refresh fails, and the expo screen
 * stops updating while still looking live. So a write that blanks both tokens
 * is ignored here: a failed refresh keeps the last refresh token and the next
 * attempt can succeed. Signing out goes through clearAuth(), which removes the
 * stored auth directly.
 */
function writeStoredAuth(data: AuthenticationData): void {
  const blanksTokens = !data?.access_token && !data?.refresh_token;
  if (blanksTokens && readStoredAuth()?.refresh_token) {
    return;
  }
  sessionStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(data));
}

@Injectable({
  providedIn: 'root',
})
export class DirectusApiService {
  private readonly fetchImpl = inject(DIRECTUS_FETCH);

  /**
   * Set by clearAuth() until the next loginUser(). The SDK only cancels its
   * auto-refresh timer when the logout request succeeds, so a refresh already
   * under way when logout fails could otherwise write fresh tokens back after
   * they were cleared and silently sign the tablet back in.
   */
  private signedOut = false;

  /**
   * Bumped by every loginUser() and clearAuth(). A logout request can hang
   * (no network) past a fresh login; its cleanup must not then wipe the new
   * session's tokens.
   */
  private authGeneration = 0;

  private readonly storage = {
    get: readStoredAuth,
    set: (data: AuthenticationData) => {
      if (this.signedOut && (data?.access_token || data?.refresh_token)) {
        return;
      }
      writeStoredAuth(data);
    },
  };

  private _client = createDirectus<DirectusSchema>(environment.cmsDomain, {
    globals: {fetch: this.fetchImpl},
  })
    .with(rest())
    .with(
      authentication('json', {
        storage: this.storage,
      })
    )
    .with(
      realtime({
        reconnect: {
          retries: 1000,
          delay: 1000,
        },
        heartbeat: true,
      })
    );

  readonly client = this._client;

  loginUser(email: string, password: string): Observable<AuthenticationData> {
    this.authGeneration++;
    this.signedOut = false;
    return from(this._client.login(email, password));
  }

  refreshUser(): Observable<AuthenticationData> {
    return from(this._client.refresh());
  }

  logoutUser(): Observable<void> {
    return from(this.clearAuth());
  }

  /**
   * Signs out on the server when it can be reached, and always forgets the
   * stored tokens locally -- even if the logout request fails.
   */
  async clearAuth(): Promise<void> {
    const generation = ++this.authGeneration;
    this.signedOut = true;
    try {
      await this._client.logout();
    } catch {
      // The local sign-out below is what matters; the server session expires on its own.
    } finally {
      if (generation === this.authGeneration) {
        sessionStorage.removeItem(AUTH_STORAGE_KEY);
      }
    }
  }

  getCollection(collectionName: keyof DirectusSchema) {
    return from(this._client.request(readCollection(collectionName)));
  }

  getItems(collectionName: keyof DirectusSchema) {
    return from(this._client.request(readItems(collectionName)));
  }

  getSingleItem<T extends DirectusCollection>(
    collectionName: keyof DirectusSchema,
    itemID: number
  ) {
    return from(
      this._client.request(readItem(collectionName, itemID))
    ) as Observable<T>;
  }

  getQueriedItems<T extends DirectusCollection>(
    collectionName: keyof DirectusSchema,
    query: any
  ) {
    return from(
      this._client.request(readItems(collectionName, query))
    ) as Observable<T[]>;
  }

  postItem<T extends DirectusCollection>(
    collectionName: keyof DirectusSchema,
    body: Partial<T>
  ) {
    return from(
      this._client.request(createItem(collectionName, body))
    ) as Observable<T>;
  }

  getRoles() {
    return from(this._client.request(readRoles()));
  }

  getRoleByID(roleID: string) {
    return from(this._client.request(readRole(roleID)));
  }

  getUsers() {
    return from(this._client.request(readUsers())) as Observable<
      DirectusUser<DirectusSchema>[]
    >;
  }

  getUser(id: string) {
    return from(this._client.request(readUser(id))) as Observable<
      DirectusUser<DirectusSchema>
    >;
  }

  getMe() {
    return from(this._client.request(readMe())) as Observable<
      DirectusUser<DirectusSchema>
    >;
  }

  patchItem<T extends DirectusCollection>(
    id: number,
    collectionName: keyof DirectusSchema,
    body: Partial<T>
  ) {
    return from(
      this._client.request(updateItem(collectionName, id, body))
    ) as Observable<T>;
  }

  deleteItem(id: number, collectionName: keyof DirectusSchema) {
    return from(this._client.request(deleteItem(collectionName, id)));
  }
}
